// One-shot registration of every source + layer the main map needs. Idempotent
// — guarded by `getSource` so it's safe to re-run after a style.load (which is
// when MapLibre tears the layer stack down). The actual visibility/opacity per
// layer is owned by `applyLayerState`; this module just makes sure the layers
// exist so it has something to drive.
//
// Vector layers are served as PMTiles archives via the `pmtiles://` protocol
// (registered in agricultural-lulc-page.tsx). Raster layers stay XYZ tiles.

import type maplibregl from "maplibre-gl";
import {
  PMTILES_SOURCES,
  PMTILES_SOURCE_LAYERS,
  LULC_COLORS,
  LULC_CHANGE_COLORS,
  CROP_COLORS,
  FOREST_COLORS,
  LULC_RASTER_TILES,
  LULC_RASTER_MIN_ZOOM,
  LULC_RASTER_MAX_ZOOM,
  SENTINEL_RASTER_TILES,
  SENTINEL_RASTER_MIN_ZOOM,
  SENTINEL_VARIANT_MAX_ZOOM,
  CROPS_RASTER_TILES,
  CROPS_RASTER_MIN_ZOOM,
  CROPS_RASTER_MAX_ZOOM,
  HARVEST_RASTER_TILES,
  HARVEST_RASTER_MIN_ZOOM,
  HARVEST_RASTER_MAX_ZOOM,
  FOREST_RASTER_TILES,
  FOREST_RASTER_MIN_ZOOM,
  FOREST_RASTER_MAX_ZOOM,
} from "../config/agricultural-lulc";
import { buildClassColorExpr } from "./map-helpers";

// PMTiles URLs are absolute (the static asset is served from the same origin
// as the demo). MapLibre's `pmtiles://` protocol takes the full HTTPS URL
// after the protocol prefix. Building it from `window.location.origin` keeps
// it pointed at preprod/prod CloudFront in deployed builds and at localhost
// in dev — same origin as the demo, so no CORS preflight.
function pmtilesUrl(absolutePath: string): string {
  return `pmtiles://${window.location.origin}${absolutePath}`;
}

export function registerMainMapLayers(m: maplibregl.Map): void {
  registerLgaBoundary(m);
  // Sentinel is base imagery — register it first so every thematic raster
  // (LULC, crops, harvest, forest) paints on top of it. MapLibre draws layers
  // in registration order, so anything added later renders above.
  registerSentinelRasterPyramid(m);
  registerLulcVector(m, "lulc-2024", PMTILES_SOURCES.lulc2024, PMTILES_SOURCE_LAYERS.lulc2024);
  registerLulcVector(m, "lulc-2025", PMTILES_SOURCES.lulc2025, PMTILES_SOURCE_LAYERS.lulc2025);
  registerLulcRasterPyramid(m);
  registerLulcChange(m);
  registerLulcChangePatches(m);
  registerCropsParcels(m);
  registerCropsRaster(m);
  registerHarvestRaster(m);
  registerForestRaster(m);
  registerForestParcels(m);
  registerParcels(m);
}

function registerLgaBoundary(m: maplibregl.Map) {
  if (m.getSource("lga-boundary")) return;
  m.addSource("lga-boundary", {
    type: "vector",
    url: pmtilesUrl(PMTILES_SOURCES.lgaBoundary),
  });
  m.addLayer({
    id: "lga-boundary-fill",
    type: "fill",
    source: "lga-boundary",
    "source-layer": PMTILES_SOURCE_LAYERS.lgaBoundary,
    paint: { "fill-color": "#6366f1", "fill-opacity": 0.05 },
  });
  m.addLayer({
    id: "lga-boundary-line",
    type: "line",
    source: "lga-boundary",
    "source-layer": PMTILES_SOURCE_LAYERS.lgaBoundary,
    paint: {
      "line-color": "#6366f1",
      "line-width": 2.5,
      "line-dasharray": [3, 2],
    },
  });
}

function registerLulcVector(
  m: maplibregl.Map,
  sourceId: "lulc-2024" | "lulc-2025",
  url: string,
  sourceLayer: string
) {
  if (m.getSource(sourceId)) return;
  m.addSource(sourceId, { type: "vector", url: pmtilesUrl(url) });
  m.addLayer({
    id: `${sourceId}-fill`,
    type: "fill",
    source: sourceId,
    "source-layer": sourceLayer,
    paint: {
      "fill-color": buildClassColorExpr(LULC_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

// LULC raster tiles (10 m pixel-level: 2024, 2025, and the year-over-year
// change raster). Used by the LULC wizard step in place of the parcel-
// aggregated PMTiles, and by step 7 (comparison) on both sides.
function registerLulcRasterPyramid(m: maplibregl.Map) {
  const variants = ["2024", "2025", "change"] as const;
  for (const variant of variants) {
    const sourceId = `lulc-raster-${variant}`;
    if (m.getSource(sourceId)) continue;
    m.addSource(sourceId, {
      type: "raster",
      tiles: [LULC_RASTER_TILES[variant]],
      tileSize: 256,
      minzoom: LULC_RASTER_MIN_ZOOM,
      maxzoom: LULC_RASTER_MAX_ZOOM,
    });
    m.addLayer({
      id: `${sourceId}-fill`,
      type: "raster",
      source: sourceId,
      paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
      layout: { visibility: "none" },
    });
  }
}

// Sentinel-2 RGBA mosaic per year, served as XYZ WebP raster tiles. Two
// variants exist side by side ("default" z8-13 from public/, "hires" z8-15
// from S3) so we can A/B them at runtime. Powers the "Satellite Imagery"
// wizard step (step 2).
function registerSentinelRasterPyramid(m: maplibregl.Map) {
  const years = ["2024", "2025"] as const;
  const variants = ["default", "hires"] as const;
  for (const variant of variants) {
    for (const year of years) {
      const sourceId = `sentinel-${variant}-${year}`;
      if (m.getSource(sourceId)) continue;
      m.addSource(sourceId, {
        type: "raster",
        tiles: [SENTINEL_RASTER_TILES[variant][year]],
        tileSize: 256,
        minzoom: SENTINEL_RASTER_MIN_ZOOM,
        maxzoom: SENTINEL_VARIANT_MAX_ZOOM[variant],
        attribution: `Imagery: Contains modified Copernicus Sentinel data ${year}`,
      });
      m.addLayer({
        id: `sentinel-${variant}-${year}-raster`,
        type: "raster",
        source: sourceId,
        paint: { "raster-opacity": 0 },
        layout: { visibility: "none" },
      });
    }
  }
}

function registerLulcChange(m: maplibregl.Map) {
  if (m.getSource("lulc-change")) return;
  m.addSource("lulc-change", {
    type: "vector",
    url: pmtilesUrl(PMTILES_SOURCES.lulcChange),
  });
  m.addLayer({
    id: "lulc-change-fill",
    type: "fill",
    source: "lulc-change",
    "source-layer": PMTILES_SOURCE_LAYERS.lulcChange,
    filter: ["!=", ["get", "dominant_class"], "No Change"],
    paint: {
      "fill-color": buildClassColorExpr(LULC_CHANGE_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: "lulc-change-outline",
    type: "line",
    source: "lulc-change",
    "source-layer": PMTILES_SOURCE_LAYERS.lulcChange,
    paint: {
      "line-color": "#94a3b8",
      "line-width": 0.5,
      "line-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

// Raster-derived area polygons — only meaningful as an overlay when the
// parcel-aggregated change layer is visible.
function registerLulcChangePatches(m: maplibregl.Map) {
  if (m.getSource("lulc-change-patches")) return;
  m.addSource("lulc-change-patches", {
    type: "vector",
    url: pmtilesUrl(PMTILES_SOURCES.lulcChangePatches),
  });
  m.addLayer({
    id: "lulc-change-patches-fill",
    type: "fill",
    source: "lulc-change-patches",
    "source-layer": PMTILES_SOURCE_LAYERS.lulcChangePatches,
    paint: {
      "fill-color": buildClassColorExpr(LULC_CHANGE_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

// Winter 2025 parcel-aggregated crops (Wheat/Barley/Canola). ~5,400 of 6,200
// parcels have no crop and come through with dominant_class=null; filtered
// out so they don't drown the map in fallback grey.
function registerCropsParcels(m: maplibregl.Map) {
  if (m.getSource("crops-2025")) return;
  m.addSource("crops-2025", {
    type: "vector",
    url: pmtilesUrl(PMTILES_SOURCES.crops2025),
  });
  m.addLayer({
    id: "crops-2025-fill",
    type: "fill",
    source: "crops-2025",
    "source-layer": PMTILES_SOURCE_LAYERS.crops2025,
    filter: ["in", ["get", "dominant_class"], ["literal", ["Wheat", "Canola", "Barley"]]],
    paint: {
      "fill-color": buildClassColorExpr(CROP_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

// Summer 2024 pixel-level raster (Sorghum/Cotton/Other Crop/Fallow).
// Different season from the parcel layer above — the per-pixel source only
// exists for summer.
function registerCropsRaster(m: maplibregl.Map) {
  if (m.getSource("crops-raster-summer2024")) return;
  m.addSource("crops-raster-summer2024", {
    type: "raster",
    tiles: [CROPS_RASTER_TILES],
    tileSize: 256,
    minzoom: CROPS_RASTER_MIN_ZOOM,
    maxzoom: CROPS_RASTER_MAX_ZOOM,
  });
  m.addLayer({
    id: "crops-raster-summer2024-fill",
    type: "raster",
    source: "crops-raster-summer2024",
    paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    layout: { visibility: "none" },
  });
}

// Same source AOI as crops, but each cropped class splits into harvested vs
// standing using harvest_2_<class>.tif from the same delivery.
function registerHarvestRaster(m: maplibregl.Map) {
  if (m.getSource("harvest-raster-summer2024")) return;
  m.addSource("harvest-raster-summer2024", {
    type: "raster",
    tiles: [HARVEST_RASTER_TILES],
    tileSize: 256,
    minzoom: HARVEST_RASTER_MIN_ZOOM,
    maxzoom: HARVEST_RASTER_MAX_ZOOM,
  });
  m.addLayer({
    id: "harvest-raster-summer2024-fill",
    type: "raster",
    source: "harvest-raster-summer2024",
    paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    layout: { visibility: "none" },
  });
}

// 2024 pixel-level forest raster (Pipeline B forest.tif). Toggled alongside
// the parcel-aggregated PMTiles via layers.forest.useRaster.
function registerForestRaster(m: maplibregl.Map) {
  if (m.getSource("forest-raster-2024")) return;
  m.addSource("forest-raster-2024", {
    type: "raster",
    tiles: [FOREST_RASTER_TILES],
    tileSize: 256,
    minzoom: FOREST_RASTER_MIN_ZOOM,
    maxzoom: FOREST_RASTER_MAX_ZOOM,
  });
  m.addLayer({
    id: "forest-raster-2024-fill",
    type: "raster",
    source: "forest-raster-2024",
    paint: { "raster-opacity": 0, "raster-resampling": "nearest" },
    layout: { visibility: "none" },
  });
}

function registerForestParcels(m: maplibregl.Map) {
  if (m.getSource("forest-2025")) return;
  m.addSource("forest-2025", {
    type: "vector",
    url: pmtilesUrl(PMTILES_SOURCES.forest2025),
  });
  m.addLayer({
    id: "forest-2025-fill",
    type: "fill",
    source: "forest-2025",
    "source-layer": PMTILES_SOURCE_LAYERS.forest2025,
    paint: {
      "fill-color": buildClassColorExpr(FOREST_COLORS),
      "fill-opacity": 0,
    },
    layout: { visibility: "none" },
  });
}

function registerParcels(m: maplibregl.Map) {
  if (m.getSource("parcels")) return;
  m.addSource("parcels", {
    type: "vector",
    url: pmtilesUrl(PMTILES_SOURCES.parcels),
  });
  m.addLayer({
    id: "parcels-fill",
    type: "fill",
    source: "parcels",
    "source-layer": PMTILES_SOURCE_LAYERS.parcels,
    // Click hit-detection layer — invisible but queryable.
    paint: { "fill-opacity": 0 },
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: "parcels-line",
    type: "line",
    source: "parcels",
    "source-layer": PMTILES_SOURCE_LAYERS.parcels,
    paint: {
      "line-color": "#a78bfa",
      "line-width": 1.5,
      "line-opacity": 0,
    },
    layout: { visibility: "none" },
  });
  m.addLayer({
    id: "parcels-highlight",
    type: "line",
    source: "parcels",
    "source-layer": PMTILES_SOURCE_LAYERS.parcels,
    paint: {
      "line-color": "#facc15",
      "line-width": 3,
      "line-opacity": 0,
    },
    filter: ["==", ["get", "cadastre_polygon_pid"], ""],
    layout: { visibility: "visible" },
  });
}
